import React from "react";
import { ArrowRight, ShieldCheck, Clock, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  return <section id="hero" data-nav="dark" className="dark bg-background text-foreground relative min-h-[92vh] lg:min-h-screen w-full flex items-center overflow-hidden">
      {/* Cinematic Background Image with Multi-tier Atmospheric Lighting Overlay */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/chicago_luxury_black_sedan_night.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/chicago_luxury_black_sedan_night.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/chicago_luxury_black_sedan_night.png-wvc-sizes" alt="Branded luxury executive sedan at night under warm Chicago amber street lighting" className="w-full h-full object-cover object-center lg:object-[64%_center] scale-105 transition-transform duration-1000 ease-out" loading="eager" decoding="async" />

        {/* Ambient Lounge Gold Glow and Obsidian Scrims */}
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/40 lg:via-background/80 lg:to-background/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-background/70 lg:via-transparent lg:to-background/60" />
        <div className="absolute top-1/4 -left-20 w-[30rem] h-[30rem] rounded-lg bg-primary/10 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-10 right-1/4 w-80 h-80 rounded-lg bg-primary/5 blur-[90px] pointer-events-none" />
      </div>

      {/* Hero Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-24 sm:py-28 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Main Narrative Column */}
          <div className="lg:col-span-8 text-left">
            {/* Flat-rate Guarantee Badge */}
            <div className="inline-flex items-center gap-2 rounded-lg border border-primary/35 bg-card/85 px-3.5 py-1.5 mb-6 backdrop-blur-md shadow-xs">
              <Sparkles className="size-4 text-primary" />
              <span className="text-xs sm:text-sm font-semibold tracking-wide text-foreground">
                Flat-Rate Private Chauffeur Service
              </span>
            </div>

            {/* Display Headline with Signature Gold Italic Emphasis */}
            <h1 className="font-default text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-foreground leading-[1.08] mb-6">
              Executive flat-rate travel across{" "}
              <span className="italic font-serif text-primary">Illinois.</span>
            </h1>

            {/* Subheading */}
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground font-normal leading-relaxed mb-8 max-w-2xl">
              Experience transparent flat pricing with no meters and zero surge charges.
              Our branded fleet of immaculate sedans and SUVs provides private executive
              transfers 24 hours a day across Illinois and neighboring states.
            </p>

            {/* Primary Action Button & Auxiliary Reassurance */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 pt-2 pb-6">
              <Button asChild size="lg" className="w-full sm:w-auto h-13 px-8 bg-primary text-primary-foreground font-semibold rounded-md shadow-lg shadow-primary/20 hover:bg-primary/90 transition-colors">
                <Link to="/OrderPage" className="inline-flex items-center justify-center gap-2.5">
                  <span>Book Your Ride</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                <Clock className="size-4 text-primary shrink-0" />
                <span>24/7 on-demand dispatch & guaranteed reservation arrival</span>
              </div>
            </div>

            {/* Reassurance Feature Highlights */}
            <div className="flex flex-wrap items-center gap-y-2 gap-x-6 pt-2 text-xs sm:text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary" />
                <span>O'Hare & Midway airport transfers</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary" />
                <span>Vetted professional chauffeurs</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary" />
                <span>All-inclusive locked rate</span>
              </div>
            </div>
          </div>

          {/* Value Matrix Side Card */}
          <div className="lg:col-span-4 w-full">
            <div className="relative rounded-lg border border-border/80 bg-card/90 backdrop-blur-md p-6 sm:p-7 shadow-xl">
              {/* Top Hairline Accent */}
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

              <div className="flex items-center justify-between pb-5 border-b border-border/70">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-5 text-primary" />
                  <span className="text-sm font-semibold text-foreground tracking-wide uppercase">
                    Service Standards
                  </span>
                </div>
                <span className="text-xs px-2.5 py-1 rounded-lg bg-primary/15 text-primary font-mono font-medium">
                  Guaranteed
                </span>
              </div>

              <div className="space-y-4 pt-5">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    Pricing
                  </p>
                  <p className="text-base sm:text-lg font-semibold text-foreground mt-0.5">
                    Zero surge pricing
                  </p>
                </div>

                <div className="pt-2 border-t border-border/50">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    Fleet
                  </p>
                  <p className="text-base sm:text-lg font-semibold text-foreground mt-0.5">
                    Branded luxury
                  </p>
                </div>

                <div className="pt-2 border-t border-border/50">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    Coverage
                  </p>
                  <p className="text-base sm:text-lg font-semibold text-foreground mt-0.5">
                    Tri-state region
                  </p>
                </div>

                <div className="pt-2 border-t border-border/50">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    Availability
                  </p>
                  <p className="text-base sm:text-lg font-semibold text-foreground mt-0.5">
                    24/7 dedicated support
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}